import { A } from '@ember/array';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { task, lastValue, all, restartableTask, timeout } from 'ember-concurrency';
import type DocumentUploadPageAdapter from 'garaje/adapters/document-upload-page';
import type { RequiredDocumentSignature } from 'garaje/components/flows/sections/required-documents/list';
import type { ReUploadConfig } from 'garaje/models/custom-user-document-template';
import type DocumentUploadPageModel from 'garaje/models/document-upload-page';
import { TEST_IDENTIFIER, VACCINE_IDENTIFIER } from 'garaje/models/user-document-template';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type LoggerService from 'garaje/services/logger';
import type MetricsService from 'garaje/services/metrics';
import type TrialService from 'garaje/services/trial';
import type UserDocumentService from 'garaje/services/user-document';
import updateAndSaveTask from 'garaje/utils/decorators/update-and-save-task';
import zft from 'garaje/utils/zero-for-tests';
import { or } from 'macro-decorators';
import { cached, localCopy } from 'tracked-toolbox';

import type { LocationOverviewSignInFlowsShowLegalDocumentsIndexRouteModel } from './route';

interface ConfigurationPayloadEntry {
  id: string;
  type: string;
  attributes: {
    'allow-llm-parsing': boolean;
    're-upload-config': ReUploadConfig | undefined;
  };
  relationships: {
    'user-document-template': {
      data: { id: string; type: string };
    };
  };
}

@updateAndSaveTask
class LocationOverviewSignInFlowsShowLegalDocumentsIndexController extends Controller {
  declare model: LocationOverviewSignInFlowsShowLegalDocumentsIndexRouteModel;

  @service declare trial: TrialService;
  @service declare logger: LoggerService;
  @service declare metrics: MetricsService;
  @service declare featureFlags: FeatureFlagsService;
  @service('user-document') declare userDocumentService: UserDocumentService;
  @service declare store: StoreService;

  @tracked _documentUploadPage: DocumentUploadPageModel | null = null;
  @tracked _configurations: UserDocumentTemplateConfigurationModel[] = [];

  @lastValue('setupTrackingConfigurations') trackingConfigurations?: {
    vaccineConfiguration?: UserDocumentTemplateConfigurationModel;
    testConfiguration?: UserDocumentTemplateConfigurationModel;
  };
  @localCopy('trackingConfigurations.vaccineConfiguration')
  vaccineConfiguration?: UserDocumentTemplateConfigurationModel;
  @localCopy('trackingConfigurations.testConfiguration') testConfiguration?: UserDocumentTemplateConfigurationModel;
  @or('testConfiguration.active', 'vaccineConfiguration.active') trackingEnabled!: boolean;

  get canAccessDocumentUploads(): boolean {
    return this.featureFlags.isEnabled('vis-prereg-document-uploads');
  }

  get documentUploadPageId(): string | null {
    return this._documentUploadPage?.id ?? null;
  }

  get documentUploadPageEnabled(): boolean {
    return this._documentUploadPage?.enabled ?? false;
  }

  @cached
  get requiredDocuments(): RequiredDocumentSignature[] {
    const docs: RequiredDocumentSignature[] = [];
    for (let index = 0; index < this._configurations.length; index++) {
      const cfg = this._configurations[index]!;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const templateId: string | undefined = cfg.belongsTo('userDocumentTemplate').id();
      const template = templateId ? this.store.peekRecord('custom-user-document-template', templateId) : null;
      // Skip configurations whose template cannot be resolved — they produce
      // ghost rows with blank titles. The list component's _newDocuments array
      // already holds the authoritative entry until the next full reload.
      if (!template) continue;
      const config = cfg.reUploadConfig;
      let reuploadDate: string | undefined;
      if (config?.policy === 'on_date_yearly' && config.month && config.day) {
        const d = new Date(new Date().getFullYear(), config.month - 1, config.day);
        reuploadDate = d.toISOString();
      }
      docs.push({
        id: template.id,
        title: template.title,
        subtitle: template.description,
        reuploadPolicy: config?.policy,
        reuploadDays: config?.days ? String(config.days) : undefined,
        reuploadDate,
        // Use backend position if available (PR #20133), fallback to insertion order
        position: cfg.position || docs.length + 1,
        configurationId: cfg.id,
        allowLlmParsing: Boolean(cfg.allowLlmParsing),
      });
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- cfg.reUploadConfig is @attr() without type transform
    return docs;
  }

  @action
  trackFeatureToggled(isEnabled: boolean): void {
    let eventName = 'Legal Docs - Legal Documents ';
    eventName += isEnabled ? 'Enabled' : 'Disabled';
    this.metrics.trackEvent(eventName, {
      company_id: this.model.tracking.currentCompanyId,
    });
  }

  loadDocumentUploadPage = task(async () => {
    if (!this.canAccessDocumentUploads) return;
    try {
      // Clear stale state from previous flow (controllers are singletons)
      this._configurations = [];
      this._documentUploadPage = null;
      // The global-flow backend doesn't expose document-upload-page in its relationships,
      // so we load it through the first local flow which does have this relationship.
      const flows = await this.model.flow.flows;
      const localFlow = flows.objectAt(0);
      if (!localFlow) return;
      // Reload the local flow with document-upload-page included so the relationship resolves
      const reloadedFlow = await this.store.findRecord('flow', localFlow.id, {
        include: 'document-upload-page',
        reload: true,
      });
      const page = await reloadedFlow.documentUploadPage;
      if (page) {
        this._documentUploadPage = page;
        // Fetch per-page configurations from the new endpoint
        const adapter = this.store.adapterFor('document-upload-page') as DocumentUploadPageAdapter;
        const response = (await adapter.fetchConfigurations(page.id)) as {
          data: ConfigurationPayloadEntry[];
          included?: { id: string; type: string; attributes: Record<string, unknown> }[];
        };
        // Push included templates into the store so peekRecord works in requiredDocuments
        for (const item of response.included ?? []) {
          this.store.pushPayload({ data: item });
        }
        // Push all configurations into the store in one pass, then peek records
        for (const entry of response.data) {
          this.store.pushPayload({ data: entry });
        }
        const configs = response.data
          .map((entry: ConfigurationPayloadEntry) =>
            this.store.peekRecord('user-document-template-configuration', entry.id),
          )
          .filter((c): c is UserDocumentTemplateConfigurationModel => c !== null);
        this._configurations = configs;
      }
    } catch (e) {
      // document-upload-page may not exist yet for this flow
      this.logger.warn('[LegalDocuments] Failed to load document-upload-page:', e as Error);
    }
  });

  setupTrackingConfigurations = task(async () => {
    if (!this.userDocumentService.loadTemplates.lastSuccessful?.value) {
      await this.userDocumentService.loadTemplates.perform();
    }

    const [vaccineConfiguration, testConfiguration] = await all([
      this.userDocumentService.getUserDocumentTemplateConfigurationForFlow(this.model.flow, VACCINE_IDENTIFIER),
      this.userDocumentService.getUserDocumentTemplateConfigurationForFlow(this.model.flow, TEST_IDENTIFIER),
    ]);

    return { vaccineConfiguration, testConfiguration };
  });

  searchUsersTask = restartableTask(async (term: string) => {
    await timeout(zft(300));
    const locations = this.store.peekAll('skinny-location') ?? [];
    const re = new RegExp(`.*${term}.*`, 'i');
    const currentContactsIds = this.model.flow.visitorDocumentContacts.content.map((contact) => contact.id);
    const userRoles = await this.store.query('user-role', {
      filter: {
        query: term,
        location: locations.mapBy('id').join(','),
        roles: ['Global Admin', 'Location Admin', 'Receptionist'].join(','),
      },
    });
    const users = await all(userRoles.map((role) => role.user));

    return A(users)
      .uniqBy('id')
      .reject(({ id }) => currentContactsIds.includes(id))
      .filter(({ fullName }) => fullName.match(re));
  });

  @action
  toggleDocumentUploadEnabled(value: boolean): void {
    const page = this._documentUploadPage;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
    if (page) void (this as any).updateAndSaveTask.perform(page, 'enabled', value, false); // decorator-injected task, types not exposed
  }

  @action
  refreshDocumentList(change?: {
    id: string;
    action: 'added' | 'removed';
    configuration?: UserDocumentTemplateConfigurationModel;
  }): void {
    if (change?.action === 'added' && change.configuration) {
      const exists = this._configurations.some((c) => c.id === change.configuration!.id);
      if (!exists) {
        this._configurations = [...this._configurations, change.configuration];
      } else {
        // Replace existing configuration (e.g. after update)
        this._configurations = this._configurations.map((c) =>
          c.id === change.configuration!.id ? change.configuration! : c,
        );
      }
    } else if (change?.action === 'removed') {
      this._configurations = this._configurations.filter((c) => {
        const templateId = c.belongsTo('userDocumentTemplate').id();
        return templateId !== change.id;
      });
    }
  }

  @action
  _trackAddLegalDocStarted(): void {
    this.send('trackAddLegalDocStarted');
  }

  @action
  _trackEditLegalDocStarted(): void {
    this.send('trackEditLegalDocStarted');
  }
}

export default LocationOverviewSignInFlowsShowLegalDocumentsIndexController;
